import React, { useEffect, useMemo, useState } from 'react';
import { Users, UserCheck, Calendar, CheckSquare, Loader2 } from 'lucide-react';
import { adminDashboardApi, adminReportsApi, AdminReportPeriodType, AdminReportSummary } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { BarChart as RechartsBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import PaymentRevenueWidget from '../../../components/dashboard/PaymentRevenueWidget';
import PaymentForecastWidget from '../../../components/dashboard/PaymentForecastWidget';
import { PaymentAnomaliesContent } from '../../../components/dashboard/PaymentAnomaliesWidget';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { AlertTriangle } from 'lucide-react';

const AdminDashboard = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);
  const [overview, setOverview] = useState<Awaited<ReturnType<typeof adminDashboardApi.getOverview>> | null>(null);
  const [reportPeriodType, setReportPeriodType] = useState<AdminReportPeriodType>('quarter');
  const [reportPeriod, setReportPeriod] = useState<string>('');
  const [reportData, setReportData] = useState<AdminReportSummary | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const quarterOptions = (() => {
    const today = new Date();
    const year = today.getFullYear();
    return [year - 1, year].flatMap((y) => [1, 2, 3, 4].map((q) => ({
      value: `${y}-Q${q}`,
      label: `${y} Q${q}`,
    })));
  })();
  const yearOptions = (() => {
    const year = new Date().getFullYear();
    return [year - 2, year - 1, year].map((y) => ({
      value: String(y),
      label: String(y),
    }));
  })();

  const formatEuro = (cents: number): string => {
    const value = (cents || 0) / 100;
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(value);
  };
  const formatCompactEuro = (cents: number): string => {
    const value = (cents || 0) / 100;
    return new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 }).format(value) + ' EUR';
  };

  const stats = useMemo(() => {
    if (!overview) return [];
    return [
      { title: 'Aktive Schüler', value: String(overview.kpis.activeStudents), icon: <Users size={20} /> },
      { title: 'Aktive Lehrer', value: String(overview.kpis.activeTeachers), icon: <UserCheck size={20} /> },
      { title: 'Probestunden-Anfragen', value: String(overview.kpis.trialRequests), icon: <Calendar size={20} /> },
      { title: 'Offene Aufgaben', value: String(overview.kpis.openTasks), icon: <CheckSquare size={20} /> },
    ];
  }, [overview]);
  const locations = overview?.locations || [];
  const revenueSeries = overview?.charts.revenueByMonth || [];
  const occupancySeries = overview?.charts.occupancyByWeekday || [];

  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      setDashboardError(null);
      try {
        const data = await adminDashboardApi.getOverview();
        setOverview(data);
      } catch (error) {
        setDashboardError(error instanceof Error ? error.message : 'Dashboard-Daten konnten nicht geladen werden');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, []);
  useEffect(() => {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    if (reportPeriodType === 'quarter') {
      setReportPeriod(`${now.getFullYear()}-Q${quarter}`);
      return;
    }
    setReportPeriod(String(now.getFullYear()));
  }, [reportPeriodType]);
  const loadReport = async () => {
    if (!reportPeriod) return;
    setReportLoading(true);
    setReportError(null);
    try {
      const report = await adminReportsApi.getSummary(reportPeriodType, reportPeriod);
      setReportData(report);
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'Bericht konnte nicht geladen werden');
    } finally {
      setReportLoading(false);
    }
  };
  const downloadReport = async (format: 'pdf' | 'csv') => {
    if (!reportData) return;
    try {
      const url = await adminReportsApi.getDownloadUrl(reportData.reportId, format);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      setReportError(error instanceof Error ? error.message : 'Download fehlgeschlagen');
    }
  };
  if (isLoading) {
    return <div className="flex h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>;
  }
  return <div className="mx-auto max-w-7xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Admin Dashboard</h1>
        <p className="text-muted-foreground">
          Globale Übersicht aller SIDIGO Standorte
        </p>
      </div>
      {/* Stats overview */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {stats.length === 0 ? <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground sm:col-span-2 lg:col-span-4">
            Keine KPI-Daten verfügbar.
          </div> : stats.map(stat => <div key={stat.title} className="rounded-lg border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <div className="rounded-md bg-primary/10 p-2 text-primary">
                {stat.icon}
              </div>
            </div>
            <div className="mt-3">
              <p className="text-sm text-muted-foreground">{stat.title}</p>
              <h3 className="text-2xl font-bold">{stat.value}</h3>
            </div>
          </div>)}
      </div>
      {overview && <div className="rounded-lg border bg-card p-4 shadow-sm">
          <p className="text-sm text-muted-foreground">Gebuchter Umsatz (letzte 6 Monate)</p>
          <p className="mt-1 text-3xl font-semibold">{formatEuro(overview.kpis.bookedRevenueCents)}</p>
        </div>}
      {dashboardError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{dashboardError}</div>}

      {/* Stripe-basierter Umsatz + Forecast + Anomalien */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <PaymentRevenueWidget daysWindow={30} />
        <PaymentForecastWidget daysAhead={30} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              Zahlungs-Anomalien (7 Tage)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <PaymentAnomaliesContent daysWindow={7} />
          </CardContent>
        </Card>
      </div>
      {/* Locations overview */}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b p-4">
          <h2 className="font-semibold">Standorte Übersicht</h2>
        </div>
        <div className="p-4">
          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="border-b text-left text-sm text-muted-foreground">
                  <th className="pb-3 pr-4 font-medium">Standort</th>
                  <th className="pb-3 px-4 font-medium">Schüler</th>
                  <th className="pb-3 px-4 font-medium">Lehrer</th>
                  <th className="pb-3 px-4 font-medium">Auslastung</th>
                </tr>
              </thead>
              <tbody>
                {locations.length === 0 ? <tr>
                    <td className="py-6 text-center text-sm text-muted-foreground" colSpan={4}>
                      Keine Standortdaten verfügbar.
                    </td>
                  </tr> : locations.map(tenant => <tr key={tenant.id} className="border-b">
                    <td className="py-3 pr-4">
                      <div className="font-medium">{tenant.name || `Standort #${tenant.id}`}</div>
                    </td>
                    <td className="py-3 px-4">{tenant.studentsCount}</td>
                    <td className="py-3 px-4">{tenant.teachersCount}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="h-2 w-full rounded-full bg-muted">
                          <div className="h-2 rounded-full bg-primary" style={{
                        width: `${tenant.occupancyRate}%`
                      }} />
                        </div>
                        <span className="text-sm">{tenant.occupancyRate}%</span>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Reports */}
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="border-b p-4">
          <h2 className="font-semibold">Quartals- & Jahresberichte</h2>
          <p className="text-sm text-muted-foreground">
            Berichte für Schulleitung (Admin) laden und als PDF/CSV herunterladen.
          </p>
        </div>
        <div className="space-y-4 p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <label htmlFor="admin-report-type" className="mb-1 block text-sm font-medium">Typ</label>
              <select
                id="admin-report-type"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={reportPeriodType}
                onChange={(e) => setReportPeriodType(e.target.value as AdminReportPeriodType)}
              >
                <option value="quarter">Quartal</option>
                <option value="year">Jahr</option>
              </select>
            </div>
            <div>
              <label htmlFor="admin-report-period" className="mb-1 block text-sm font-medium">Zeitraum</label>
              <select
                id="admin-report-period"
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={reportPeriod}
                onChange={(e) => setReportPeriod(e.target.value)}
              >
                {(reportPeriodType === 'quarter' ? quarterOptions : yearOptions).map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={loadReport} disabled={reportLoading || !reportPeriod}>
                {reportLoading ? 'Lade Bericht...' : 'Bericht laden'}
              </Button>
            </div>
            <div className="flex items-end gap-2">
              <Button variant="outline" onClick={() => downloadReport('pdf')} disabled={!reportData || reportLoading}>
                PDF herunterladen
              </Button>
              <Button variant="outline" onClick={() => downloadReport('csv')} disabled={!reportData || reportLoading}>
                CSV herunterladen
              </Button>
            </div>
          </div>
          {reportError && <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">{reportError}</div>}
          {reportData && (
            <div className="space-y-4">
              <div className="rounded-md border bg-muted/20 p-3 text-sm">
                <strong>{reportData.periodLabel}</strong> ({reportData.range.startDate} bis {reportData.range.endDate})
              </div>
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-3">
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Aktive Schüler</p>
                  <p className="text-xl font-semibold">{reportData.kpis.activeStudents}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Aktive Lehrer</p>
                  <p className="text-xl font-semibold">{reportData.kpis.activeTeachers}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Probestunden-Anfragen</p>
                  <p className="text-xl font-semibold">{reportData.kpis.trialRequests}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Kündigungen</p>
                  <p className="text-xl font-semibold">{reportData.kpis.cancellations}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Neue Anmeldungen</p>
                  <p className="text-xl font-semibold">{reportData.kpis.newEnrollments}</p>
                </div>
                <div className="rounded-md border p-3">
                  <p className="text-xs text-muted-foreground">Umsatz berechnet</p>
                  <p className="text-xl font-semibold">{formatEuro(reportData.kpis.bookedRevenueCents)}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    eingegangen {formatEuro(reportData.kpis.receivedRevenueCents ?? 0)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="px-3 py-2 text-left">Standort</th>
                        <th className="px-3 py-2 text-right">Aktive Schüler</th>
                        <th className="px-3 py-2 text-right">Anmeldungen</th>
                        <th className="px-3 py-2 text-right">Anfragen</th>
                        <th className="px-3 py-2 text-right">Kündigungen</th>
                        <th className="px-3 py-2 text-right">Berechnet</th>
                        <th className="px-3 py-2 text-right">Eingegangen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.details.byLocation.map((row) => (
                        <tr key={row.standortId} className="border-t">
                          <td className="px-3 py-2">{row.standortName}</td>
                          <td className="px-3 py-2 text-right">{row.activeStudents}</td>
                          <td className="px-3 py-2 text-right">{row.newEnrollments}</td>
                          <td className="px-3 py-2 text-right">{row.trialRequests}</td>
                          <td className="px-3 py-2 text-right">{row.cancellations}</td>
                          <td className="px-3 py-2 text-right">{formatEuro(row.bookedRevenueCents ?? 0)}</td>
                          <td className="px-3 py-2 text-right">{formatEuro(row.receivedRevenueCents ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40">
                      <tr>
                        <th className="px-3 py-2 text-left">Lehrkraft</th>
                        <th className="px-3 py-2 text-right">Aktive Schüler</th>
                        <th className="px-3 py-2 text-right">Anmeldungen</th>
                        <th className="px-3 py-2 text-right" title="Anfragen, aus denen eine Probestunde bei der Lehrkraft wurde">
                          Probestd. aus Anfragen
                        </th>
                        <th className="px-3 py-2 text-right">Kündigungen</th>
                        <th className="px-3 py-2 text-right">Berechnet</th>
                        <th className="px-3 py-2 text-right">Eingegangen</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.details.byTeacher.map((row) => (
                        <tr key={row.teacherId} className="border-t">
                          <td className="px-3 py-2">{row.teacherName || `Lehrkraft #${row.teacherId}`}</td>
                          <td className="px-3 py-2 text-right">{row.activeStudents}</td>
                          <td className="px-3 py-2 text-right">{row.newEnrollments}</td>
                          <td className="px-3 py-2 text-right">{row.trialRequests ?? 0}</td>
                          <td className="px-3 py-2 text-right">{row.cancellations}</td>
                          <td className="px-3 py-2 text-right">{formatEuro(row.bookedRevenueCents ?? 0)}</td>
                          <td className="px-3 py-2 text-right">{formatEuro(row.receivedRevenueCents ?? 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Charts */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Revenue Chart */}
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="border-b p-4">
            <h2 className="font-semibold">Monatlicher Umsatz</h2>
          </div>
          <div className="p-4">
            <div className="h-80">
              {revenueSeries.length === 0 ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Keine Umsatzdaten verfügbar.
                </div> : <ResponsiveContainer width="100%" height="100%">
                  <RechartsBarChart data={revenueSeries} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis tickFormatter={(value) => formatCompactEuro(Number(value) || 0)} />
                    <Tooltip labelFormatter={(label) => `Monat: ${label}`} formatter={(value: number) => formatEuro(Number(value) || 0)} />
                    <Bar dataKey="bookedRevenueCents" fill="#4f46e5" name="Gebuchter Umsatz" />
                  </RechartsBarChart>
                </ResponsiveContainer>}
            </div>
          </div>
        </div>
        {/* Occupancy Chart */}
        <div className="rounded-lg border bg-card shadow-sm">
          <div className="border-b p-4">
            <h2 className="font-semibold">Wöchentliche Auslastung (%)</h2>
          </div>
          <div className="p-4">
            <div className="h-80">
              {occupancySeries.length === 0 ? <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                  Keine Auslastungsdaten verfügbar.
                </div> : <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={occupancySeries} margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 5
              }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" />
                    <YAxis domain={[0, 100]} />
                    <Tooltip labelFormatter={(label) => `Wochentag: ${label}`} formatter={(value: number) => `${Math.round(Number(value) || 0)}%`} />
                    <Line type="monotone" dataKey="occupancyRate" stroke="#06b6d4" name="Auslastung" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>}
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default AdminDashboard;